import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  Link,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { reportService, VendorAssignmentReportEntry } from '../services/reportService';
import { useAuth } from '../contexts/AuthContext';
import { firmAPI } from '../services/api';

const VendorAssignmentReport: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Check if user is authorized (PMP Admin or Site Admin)
  const isAuthorized = () => {
    if (!user?.roles) return false;
    return user.roles.some(role => role.slug === 'pmp_admin' || role.slug === 'site_admin');
  };

  useEffect(() => {
    if (user && !isAuthorized()) {
      navigate('/', { replace: true });
    }
  }, [user, navigate]);

  // Load vendor firms for filter
  useEffect(() => {
    const loadVendorFirms = async () => {
      try {
        const res = await firmAPI.getAll({ type: 'vendor', status: 'approved', per_page: 1000 });
        const vendors = (res.data.data?.data || res.data.data || []).filter((f: any) => f.type === 'vendor');
        setVendorFirms(vendors);
      } catch (err) {
        console.error('Failed to load vendor firms:', err);
      }
    };
    if (isAuthorized()) {
      loadVendorFirms();
    }
  }, [user]);

  // Filter state
  const [filterTitle, setFilterTitle] = useState<string>('');
  const [filterSubtitle, setFilterSubtitle] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [projectStatus, setProjectStatus] = useState<string>('');
  const [vendorFirmId, setVendorFirmId] = useState<number | ''>('');

  // Vendor firms for filter
  const [vendorFirms, setVendorFirms] = useState<any[]>([]);

  // Data state
  const [reportData, setReportData] = useState<VendorAssignmentReportEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(30);

  const handleSearch = async () => {
    setLoading(true);
    setError('');
    try {
      const params: any = {};
      
      if (filterTitle) {
        params.filter_title = filterTitle;
      }
      
      if (filterSubtitle) {
        params.filter_subtitle = filterSubtitle;
      }

      if (startDate) {
        params.start_date = startDate;
      }

      if (endDate) {
        params.end_date = endDate;
      }

      if (projectStatus) {
        params.project_status = projectStatus;
      }

      if (vendorFirmId) {
        params.vendor_firm_id = vendorFirmId;
      }

      const data = await reportService.getVendorAssignmentReport(params);
      setReportData(data);
      setPage(0); // Reset to first page
    } catch (err: any) {
      console.error('Error fetching report:', err);
      setError(err.response?.data?.message || 'Failed to fetch report');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCaseClick = (caseId: number, firmId: number | null, isVendorCase: boolean = true) => {
    // Navigate to case view with firm ID
    if (firmId) {
      navigate(`/firms/${firmId}/cases/${caseId}`);
    } else {
      // Fallback: try to navigate without firm ID (may not work)
      navigate(`/cases/${caseId}`);
    }
  };

  const getStatusColor = (status: string | null) => {
    if (!status) return 'default';
    switch (status.toLowerCase()) {
      case 'open':
        return 'info';
      case 'in_progress':
        return 'warning';
      case 'completed':
        return 'success';
      case 'closed':
        return 'default';
      default:
        return 'default';
    }
  };

  // Calculate pagination
  const paginatedData = reportData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Show unauthorized message if user is not authorized
  if (user && !isAuthorized()) {
    return (
      <Box>
        <Alert severity="error">
          You do not have permission to access this report. Only PMP Admins and Site Admins can view this report.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Vendor Assignment Report
      </Typography>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Filter
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
            <Box sx={{ flex: 1, minWidth: { xs: '100%', md: '250px' } }}>
              <TextField
                fullWidth
                label="Title"
                value={filterTitle}
                onChange={(e) => setFilterTitle(e.target.value)}
                placeholder="Filter by case title"
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: { xs: '100%', md: '250px' } }}>
              <TextField
                fullWidth
                label="Sub Title"
                value={filterSubtitle}
                onChange={(e) => setFilterSubtitle(e.target.value)}
                placeholder="Filter by case subtitle"
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: { xs: '100%', md: '200px' } }}>
              <FormControl fullWidth>
                <InputLabel>Project Status</InputLabel>
                <Select
                  value={projectStatus}
                  label="Project Status"
                  onChange={(e) => setProjectStatus(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="archived">Archived</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: 1, minWidth: { xs: '100%', md: '200px' } }}>
              <FormControl fullWidth>
                <InputLabel>Vendor Base</InputLabel>
                <Select
                  value={vendorFirmId}
                  label="Vendor Base"
                  onChange={(e) => setVendorFirmId(e.target.value as number | '')}
                >
                  <MenuItem value="">All</MenuItem>
                  {vendorFirms.map((firm: any) => (
                    <MenuItem key={firm.id} value={firm.id}>
                      {firm.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
            <Box sx={{ flex: 1, minWidth: { xs: '100%', md: '200px' } }}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText="Filter by assignment date"
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: { xs: '100%', md: '200px' } }}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText="Filter by assignment date"
              />
            </Box>
            <Box sx={{ minWidth: { xs: '100%', md: '120px' } }}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={handleSearch}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                sx={{ height: '56px' }}
              >
                Search
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Results */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Result
        </Typography>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Vendor User</strong></TableCell>
                <TableCell><strong>Case ID</strong></TableCell>
                <TableCell><strong>Title</strong></TableCell>
                <TableCell><strong>Project</strong></TableCell>
                <TableCell><strong>File Number</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Client Case ID</strong></TableCell>
                <TableCell><strong>Estimate</strong></TableCell>
                <TableCell><strong>Rate</strong></TableCell>
                <TableCell><strong>Amount</strong></TableCell>
                <TableCell><strong>Finalize Date</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : paginatedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    No data available. Please select filters and click Search.
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((entry, index) => (
                  <TableRow key={`${entry.vendor_case_id}-${index}`}>
                    <TableCell>{entry.vendor_user}</TableCell>
                    <TableCell>
                      {entry.vendor_case_id ? (
                        <Link
                          component="button"
                          variant="body2"
                          onClick={() => handleCaseClick(entry.vendor_case_id!, entry.vendor_firm_id, true)}
                          sx={{ cursor: 'pointer' }}
                        >
                          {entry.vendor_case_id}
                        </Link>
                      ) : (
                        'N/A'
                      )}
                    </TableCell>
                    <TableCell>
                      {entry.vendor_case_id ? (
                        <Link
                          component="button"
                          variant="body2"
                          onClick={() => handleCaseClick(entry.vendor_case_id!, entry.vendor_firm_id, true)}
                          sx={{ cursor: 'pointer' }}
                        >
                          {entry.title}
                        </Link>
                      ) : (
                        entry.title
                      )}
                    </TableCell>
                    <TableCell>{entry.project}</TableCell>
                    <TableCell>{entry.file_number || 'N/A'}</TableCell>
                    <TableCell>
                      {entry.status_label && entry.status_label !== 'N/A' ? (
                        <Chip 
                          label={entry.status_label} 
                          color={getStatusColor(entry.status) as any}
                          size="small"
                        />
                      ) : (
                        'N/A'
                      )}
                    </TableCell>
                    <TableCell>
                      {entry.client_case_id ? (
                        <Link
                          component="button"
                          variant="body2"
                          onClick={() => handleCaseClick(entry.client_case_id!, entry.client_firm_id, false)}
                          sx={{ cursor: 'pointer' }}
                        >
                          {entry.client_case_id}
                        </Link>
                      ) : (
                        'N/A'
                      )}
                    </TableCell>
                    <TableCell>
                      {entry.estimate ? (
                        `${entry.estimate.quantity} ${entry.estimate.billing_type_label}`
                      ) : (
                        'N/A'
                      )}
                    </TableCell>
                    <TableCell>{entry.rate}</TableCell>
                    <TableCell>${entry.amount}</TableCell>
                    <TableCell>{entry.finalize_date || 'N/A'}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {reportData.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Showing {page * rowsPerPage + 1} to{' '}
              {Math.min((page + 1) * rowsPerPage, reportData.length)} of {reportData.length} entries
            </Typography>
            <TablePagination
              component="div"
              count={reportData.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 30, 50, 100]}
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default VendorAssignmentReport;

